import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const FilterSidebar = ({ filters, onFiltersChange, isOpen, onClose }) => {
  const [priceRange, setPriceRange] = useState([0, 500]);
  const [expandedSections, setExpandedSections] = useState({
    category: true,
    brand: true,
    price: true,
    rating: true,
    availability: true
  });

  const categories = [
    { id: 'cleaning-supplies', name: 'Cleaning Supplies', count: 45 },
    { id: 'equipment', name: 'Equipment', count: 23 },
    { id: 'chemicals', name: 'Chemicals', count: 31 },
    { id: 'tools', name: 'Tools', count: 18 },
    { id: 'accessories', name: 'Accessories', count: 27 }
  ];

  const brands = [
    { id: 'clean360', name: 'Clean360 Pro', count: 34 },
    { id: 'ecogreen', name: 'EcoGreen', count: 28 },
    { id: 'powerclean', name: 'PowerClean', count: 22 },
    { id: 'naturefresh', name: 'NatureFresh', count: 19 },
    { id: 'industrial-grade', name: 'Industrial Grade', count: 15 }
  ];

  const ratings = [
    { value: 4, label: '4+ Stars', count: 89 },
    { value: 3, label: '3+ Stars', count: 124 },
    { value: 2, label: '2+ Stars', count: 156 },
    { value: 1, label: '1+ Stars', count: 178 }
  ];

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev?.[section]
    }));
  };

  const handleCategoryChange = (categoryId, checked) => {
    const newCategories = checked
      ? [...(filters?.categories || []), categoryId]
      : (filters?.categories || [])?.filter(id => id !== categoryId);
    
    onFiltersChange({
      ...filters,
      categories: newCategories
    });
  };

  const handleBrandChange = (brandId, checked) => {
    const newBrands = checked
      ? [...(filters?.brands || []), brandId]
      : (filters?.brands || [])?.filter(id => id !== brandId);
    
    onFiltersChange({
      ...filters,
      brands: newBrands
    });
  };

  const handlePriceChange = (newRange) => {
    setPriceRange(newRange);
    onFiltersChange({
      ...filters,
      priceRange: newRange
    });
  };

  const handleRatingChange = (rating) => {
    onFiltersChange({
      ...filters,
      minRating: filters?.minRating === rating ? null : rating
    });
  };

  const handleAvailabilityChange = (type, checked) => {
    onFiltersChange({
      ...filters,
      [type]: checked
    });
  };

  const clearAllFilters = () => {
    onFiltersChange({});
    setPriceRange([0, 500]);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters?.categories?.length) count += filters?.categories?.length;
    if (filters?.brands?.length) count += filters?.brands?.length;
    if (filters?.priceRange) count += 1;
    if (filters?.minRating) count += 1;
    if (filters?.inStock) count += 1;
    if (filters?.onSale) count += 1;
    return count;
  };

  const FilterSection = ({ title, sectionKey, children }) => (
    <div className="border-b border-border pb-4 mb-4">
      <button
        onClick={() => toggleSection(sectionKey)}
        className="flex items-center justify-between w-full text-left mb-3"
      >
        <h3 className="font-medium text-text-primary">{title}</h3>
        <Icon 
          name={expandedSections?.[sectionKey] ? "ChevronUp" : "ChevronDown"} 
          size={16} 
        />
      </button>
      {expandedSections?.[sectionKey] && children}
    </div>
  );

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      {/* Sidebar */}
      <div className={`
        fixed lg:static top-0 left-0 h-full w-80 bg-white border-r border-border z-50 
        transform transition-transform duration-300 lg:transform-none overflow-y-auto
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border lg:hidden">
          <h2 className="text-lg font-semibold text-text-primary">Filters</h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        <div className="p-4">
          {/* Filter Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2">
              <Icon name="Filter" size={20} />
              <span className="font-semibold text-text-primary">Filters</span>
              {getActiveFilterCount() > 0 && (
                <span className="bg-primary text-white text-xs font-medium px-2 py-1 rounded-full">
                  {getActiveFilterCount()}
                </span>
              )}
            </div>
            {getActiveFilterCount() > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAllFilters}
                className="text-text-secondary hover:text-error"
              >
                Clear All
              </Button>
            )}
          </div>

          {/* Categories */}
          <FilterSection title="Categories" sectionKey="category">
            <div className="space-y-2">
              {categories?.map((category) => (
                <div key={category?.id} className="flex items-center justify-between">
                  <Checkbox
                    label={category?.name}
                    checked={filters?.categories?.includes(category?.id) || false}
                    onChange={(e) => handleCategoryChange(category?.id, e?.target?.checked)}
                  />
                  <span className="text-sm text-text-secondary">
                    {category?.count}
                  </span>
                </div>
              ))}
            </div>
          </FilterSection>

          {/* Brands */}
          <FilterSection title="Brands" sectionKey="brand">
            <div className="space-y-2">
              {brands?.map((brand) => (
                <div key={brand?.id} className="flex items-center justify-between">
                  <Checkbox
                    label={brand?.name}
                    checked={filters?.brands?.includes(brand?.id) || false}
                    onChange={(e) => handleBrandChange(brand?.id, e?.target?.checked)}
                  />
                  <span className="text-sm text-text-secondary">
                    {brand?.count}
                  </span>
                </div>
              ))}
            </div>
          </FilterSection>

          {/* Price Range */}
          <FilterSection title="Price Range" sectionKey="price">
            <div className="space-y-4">
              <div className="flex items-center justify-between text-sm">
                <span>${priceRange?.[0]}</span>
                <span>${priceRange?.[1]}</span>
              </div>
              <div className="relative">
                <input
                  type="range"
                  min="0"
                  max="500"
                  value={priceRange?.[1]}
                  onChange={(e) => handlePriceChange([priceRange?.[0], parseInt(e?.target?.value)])}
                  className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
                />
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  value={priceRange?.[0]}
                  onChange={(e) => handlePriceChange([parseInt(e?.target?.value), priceRange?.[1]])}
                  className="w-20 px-2 py-1 text-sm border border-border rounded"
                  min="0"
                  max="500"
                />
                <span className="text-text-secondary">to</span>
                <input
                  type="number"
                  value={priceRange?.[1]}
                  onChange={(e) => handlePriceChange([priceRange?.[0], parseInt(e?.target?.value)])}
                  className="w-20 px-2 py-1 text-sm border border-border rounded"
                  min="0"
                  max="500"
                />
              </div>
            </div>
          </FilterSection>

          {/* Rating */}
          <FilterSection title="Customer Rating" sectionKey="rating">
            <div className="space-y-2">
              {ratings?.map((rating) => (
                <button
                  key={rating?.value}
                  onClick={() => handleRatingChange(rating?.value)}
                  className={`flex items-center justify-between w-full p-2 rounded transition-colors duration-200 ${
                    filters?.minRating === rating?.value
                      ? 'bg-primary/10 border border-primary' :'hover:bg-muted'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center">
                      {Array.from({ length: 5 }, (_, i) => (
                        <Icon
                          key={i}
                          name="Star"
                          size={14}
                          color={i < rating?.value ? "#F59E0B" : "#E5E7EB"}
                          className={i < rating?.value ? "fill-current" : ""}
                        />
                      ))}
                    </div>
                    <span className="text-sm">{rating?.label}</span>
                  </div>
                  <span className="text-sm text-text-secondary">
                    {rating?.count}
                  </span>
                </button>
              ))}
            </div>
          </FilterSection>

          {/* Availability */}
          <FilterSection title="Availability" sectionKey="availability">
            <div className="space-y-3">
              <Checkbox
                label="In Stock Only"
                checked={filters?.inStock || false}
                onChange={(e) => handleAvailabilityChange('inStock', e?.target?.checked)}
              />
              <Checkbox
                label="On Sale"
                checked={filters?.onSale || false}
                onChange={(e) => handleAvailabilityChange('onSale', e?.target?.checked)}
              />
              <Checkbox
                label="New Arrivals"
                checked={filters?.newArrivals || false}
                onChange={(e) => handleAvailabilityChange('newArrivals', e?.target?.checked)}
              />
              <Checkbox
                label="Free Shipping"
                checked={filters?.freeShipping || false}
                onChange={(e) => handleAvailabilityChange('freeShipping', e?.target?.checked)}
              />
            </div>
          </FilterSection>
        </div>
      </div>
    </>
  );
};

export default FilterSidebar;